import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './services.css';

// Constants
const PROCESS_STEPS = [
  {
    step: "01",
    title: "Discovery & Planning",
    description: "We analyze your requirements and create a comprehensive project roadmap."
  },
  {
    step: "02",
    title: "Design & Prototyping",
    description: "Our team creates intuitive designs and interactive prototypes for validation."
  },
  {
    step: "03",
    title: "Development & Testing",
    description: "We build your solution using agile methodology with continuous testing."
  },
  {
    step: "04",
    title: "Deployment & Support",
    description: "We deploy your solution and provide ongoing maintenance and support."
  }
];

const TECHNOLOGIES = [
  "React", "Node.js", "Python", "AWS", "Docker", "Kubernetes",
  "TypeScript", "Next.js", "PostgreSQL", "MongoDB", "Redis", "GraphQL"
];

const AOS_CONFIG = {
  duration: 1000,
  easing: 'ease-in-out',
  once: true,
};

// Loading Component
const LoadingState = ({ message = "Loading services..." }) => (
    <div className="services-loading">
      <p>{message}</p>
    </div>
);

// Hero Section Component
const HeroSection = () => (
    <section className="hero-section" data-aos="fade-in">
      <div className="hero-container">
        <h1 className="hero-title">
          Empowering Your <span className="gradient-text">Digital Journey</span>
        </h1>
        <p className="hero-description">
          Explore our range of services designed to help you achieve success in the digital world.
        </p>
      </div>
    </section>
);

// Service Card Component
const ServiceCard = ({ service, index, onServiceClick }) => {
  const handleClick = useCallback(() => {
    onServiceClick(service.id);
  }, [service.id, onServiceClick]);

  const handleButtonClick = useCallback((e) => {
    e.stopPropagation();
    onServiceClick(service.id);
  }, [service.id, onServiceClick]);

  return (
      <div
          className="service-card"
          data-aos="flip-right"
          data-aos-delay={index * 100}
          onClick={handleClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          aria-label={`Learn more about ${service.name}`}
      >
        <img
            src={service.image}
            alt={service.name}
            className="service-image"
            loading="lazy"
        />
        <div className="service-desc">
          <h3>{service.name}</h3>
          <p>{service.text}</p>
          <button
              onClick={handleButtonClick}
              aria-label={`Learn more about ${service.name}`}
              className="service-btn"
          >
            Details
          </button>
        </div>
      </div>
  );
};

// Services Grid Component
const ServicesGrid = ({ data, onServiceClick }) => (
    <section className="services-grid-section">
      <div className="services-container">
        <div className="section-title" data-aos="fade-up">
          <h2>Our Services</h2>
          <p>We offer a variety of services to meet your needs. Explore our range of solutions designed to help you succeed.</p>
        </div>

        <div className="services-grid">
          {data.map((service, index) => (
              <ServiceCard
                  key={`${service.name}-${service.id || index}`}
                  service={service}
                  index={index}
                  onServiceClick={onServiceClick}
              />
          ))}
        </div>
      </div>
    </section>
);

// Process Step Component
const ProcessStep = ({ step, index }) => (
    <div
        className="process-step"
        data-aos="fade-up"
        data-aos-delay={index * 100}
    >
      <div className="process-number">{step.step}</div>
      <h3>{step.title}</h3>
      <p>{step.description}</p>
    </div>
);

// Process Section Component
const ProcessSection = () => (
    <section className="process-section">
      <div className="services-container">
        <div className="process-title" data-aos="fade-up">
          <h2>Our Process</h2>
          <p>
            We follow a proven methodology to ensure successful project delivery and client satisfaction.
          </p>
        </div>

        <div className="process-grid">
          {PROCESS_STEPS.map((step, index) => (
              <ProcessStep
                  key={step.step}
                  step={step}
                  index={index}
              />
          ))}
        </div>
      </div>
    </section>
);

// Technology Item Component
const TechItem = ({ tech, index }) => (
    <div
        className="tech-item"
        data-aos="zoom-in"
        data-aos-delay={index * 50}
    >
      <span>{tech}</span>
    </div>
);

// Technologies Section Component
const TechnologiesSection = () => (
    <section className="technologies-section">
      <div className="services-container">
        <div className="technologies-title" data-aos="fade-up">
          <h2>Technologies We Use</h2>
          <p>
            We leverage cutting-edge technologies to build robust, scalable, and future-ready solutions.
          </p>
        </div>

        <div className="technologies-grid">
          {TECHNOLOGIES.map((tech, index) => (
              <TechItem
                  key={tech}
                  tech={tech}
                  index={index}
              />
          ))}
        </div>
      </div>
    </section>
);

// CTA Section Component
const CTASection = ({ onQuoteRequest, onConsultation }) => (
    <section className="cta-section">
      <div className="services-container">
        <div data-aos="fade-up">
          <h2 className="cta-title">Ready to Get Started?</h2>
          <p className="cta-description">
            Let's discuss your project requirements and create a solution that drives your business forward.
          </p>
          <div className="cta-buttons">
            <button
                className="cta-button primary"
                onClick={onQuoteRequest}
                aria-label="Request a Quote"
            >
              Request a Quote
            </button>
            <button
                className="cta-button secondary"
                onClick={onConsultation}
                aria-label="Schedule Consultation"
            >
              Schedule Consultation
            </button>
          </div>
        </div>
      </div>
    </section>
);

// Main Services Component
export const Services = ({ data }) => {
  const navigate = useNavigate();

  // Initialize AOS
  useEffect(() => {
    AOS.init(AOS_CONFIG);
  }, []);

  // Navigation handlers
  const handleServiceClick = useCallback((serviceId) => {
    navigate(`/service/${serviceId}`);
  }, [navigate]);

  const handleQuoteRequest = useCallback(() => {
    navigate('/contact');
  }, [navigate]);

  const handleConsultation = useCallback(() => {
    navigate('/contact');
  }, [navigate]);

  // Loading states
  if (!data) {
    return <LoadingState />;
  }

  if (data.length === 0) {
    return <LoadingState message="No services available at the moment." />;
  }

  return (
      <div className="services-page-wrapper">
        <div id="services" className="services-main-content">
          <HeroSection />

          <ServicesGrid
              data={data}
              onServiceClick={handleServiceClick}
          />

          <ProcessSection />

          <TechnologiesSection />

          <CTASection
              onQuoteRequest={handleQuoteRequest}
              onConsultation={handleConsultation}
          />
        </div>
      </div>
  );
};

export default Services;